import { useState, useEffect } from 'react';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { useGetA2AIpAllowlist } from '@/hooks/queries/use-get-a2a-ip-allowlist';
import { useUpdateA2AIpAllowlist } from '@/hooks/mutations/a2a-ip-allowlist/use-update-a2a-ip-allowlist';
import { useDeleteA2AIpAllowlist } from '@/hooks/mutations/a2a-ip-allowlist/use-delete-a2a-ip-allowlist';
import { isLikelyIpOrCidr } from '@/utils/is-likely-ip-or-cidr';
import { formatDateTime } from '@/utils/format-date';
import * as Sentry from '@sentry/react';

// Mirrors the server-side cap; the server rejects anything above this.
const MAX_ENTRIES = 256;

const HELP_TEXT_ID = 'a2a-ip-allowlist-help';
const INVALID_ERROR_ID = 'a2a-ip-allowlist-invalid-error';
const EMPTY_ERROR_ID = 'a2a-ip-allowlist-empty-error';
const LIMIT_ERROR_ID = 'a2a-ip-allowlist-limit-error';

const parseEntries = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

export const A2AIpAllowlistPage = () => {
  const notification = useNotification();
  const {
    data: allowlist,
    isLoading,
    error: loadError,
  } = useGetA2AIpAllowlist();
  const updateAllowlist = useUpdateA2AIpAllowlist();
  const deleteAllowlist = useDeleteA2AIpAllowlist();

  const [enabled, setEnabled] = useState(false);
  const [cidrsText, setCidrsText] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    // Wait for the fetch to settle so a refetch can't blank the form mid-edit.
    if (isLoading) return;
    if (allowlist) {
      setEnabled(allowlist.enabled);
      setCidrsText(allowlist.cidrs.join('\n'));
    } else {
      // No config for this org — clear rather than leaving the previously
      // selected org's CIDRs on screen, which invites saving them here.
      setEnabled(false);
      setCidrsText('');
    }
  }, [allowlist, isLoading]);

  const entries = parseEntries(cidrsText);
  const invalidEntries = entries.filter((entry) => !isLikelyIpOrCidr(entry));
  const tooManyEntries = entries.length > MAX_ENTRIES;
  const missingEntries = enabled && entries.length === 0;
  // Block saving when the current config failed to load: a PUT is a full
  // replace, so saving the empty default form would wipe the org's allowlist.
  const canSave =
    invalidEntries.length === 0 &&
    !tooManyEntries &&
    !missingEntries &&
    !loadError;

  const describedBy = [
    HELP_TEXT_ID,
    ...(invalidEntries.length > 0 ? [INVALID_ERROR_ID] : []),
    ...(missingEntries ? [EMPTY_ERROR_ID] : []),
    ...(tooManyEntries ? [LIMIT_ERROR_ID] : []),
  ].join(' ');

  const handleSave = async () => {
    try {
      await updateAllowlist.mutateAsync({ enabled, cidrs: entries });
      notification.success('A2A IP allowlist saved');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'a2a-ip-allowlist-save' },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to save A2A IP allowlist',
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAllowlist.mutateAsync();
      notification.success('A2A IP allowlist removed');
      setIsDeleteModalOpen(false);
      setEnabled(false);
      setCidrsText('');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'a2a-ip-allowlist-delete' },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to remove A2A IP allowlist',
      );
    }
  };

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <PageTitle id='a2a-ip-allowlist-title' title='A2A IP Allowlist' />
        <PageDescription>
          Restrict your organization's agent-to-agent (A2A) API to requests from
          the source IPs listed below. This only affects /a2a routes — it never
          applies to the platform UI or management APIs.
        </PageDescription>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='space-y-6 max-w-lg'>
          {loadError && (
            <p className='text-sm text-destructive break-words'>
              Could not load the current allowlist: {loadError.message}. Saving
              now would replace whatever is configured.
            </p>
          )}

          <div className='flex items-center gap-3'>
            <Switch
              id='a2a-ip-allowlist-enabled'
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <Label htmlFor='a2a-ip-allowlist-enabled'>
              Enforce IP allowlist
            </Label>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='a2a-ip-allowlist-cidrs'>
              Allowed IPs / CIDR ranges (one per line, IPv4 or IPv6)
            </Label>
            <Textarea
              id='a2a-ip-allowlist-cidrs'
              rows={8}
              placeholder={'203.0.113.0/24\n198.51.100.7'}
              value={cidrsText}
              onChange={(e) => setCidrsText(e.target.value)}
              aria-invalid={invalidEntries.length > 0 || tooManyEntries}
              aria-describedby={describedBy}
            />
            <p id={HELP_TEXT_ID} className='text-xs text-muted-foreground'>
              Entries are normalized on save — bare IPs become /32 or /128, and
              host bits are masked to the network address. Up to {MAX_ENTRIES}{' '}
              entries.
            </p>
            {invalidEntries.length > 0 && (
              <p
                id={INVALID_ERROR_ID}
                className='text-sm text-destructive break-words'
              >
                Invalid entries: {invalidEntries.join(', ')}
              </p>
            )}
            {missingEntries && (
              <p id={EMPTY_ERROR_ID} className='text-sm text-destructive'>
                At least one IP or CIDR range is required when enforcement is
                enabled.
              </p>
            )}
            {tooManyEntries && (
              <p id={LIMIT_ERROR_ID} className='text-sm text-destructive'>
                Too many entries: {entries.length}. The limit is {MAX_ENTRIES}.
              </p>
            )}
          </div>

          <div className='flex items-center gap-3 pt-2'>
            <Button
              onClick={handleSave}
              disabled={!canSave || updateAllowlist.isPending}
            >
              {updateAllowlist.isPending ? (
                <>
                  <LoadingSpinner size='sm' /> Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
            {allowlist && (
              <Button
                variant='outline'
                className='text-destructive hover:text-destructive'
                disabled={deleteAllowlist.isPending}
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Remove Allowlist
              </Button>
            )}
          </div>

          {allowlist?.updated_at && (
            <p className='text-xs text-muted-foreground break-words'>
              Last updated {formatDateTime(allowlist.updated_at)}
              {allowlist.updated_by_user_id &&
                ` by ${allowlist.updated_by_user_id}`}
            </p>
          )}

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            isLoading={deleteAllowlist.isPending}
            title='Remove A2A IP allowlist'
            description='Are you sure you want to remove the A2A IP allowlist? A2A API requests will no longer be restricted by source IP.'
            buttonLabel='Remove allowlist'
            handleDelete={handleDelete}
            handleCancel={() => setIsDeleteModalOpen(false)}
          />
        </div>
      )}
    </div>
  );
};
